// FILE: src/components/Login.js
import React, { useState, useEffect } from "react";
import { auth } from "../firebase";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Pull auth state from context so we can auto-redirect to Home
  const { user, loading } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [resetErr, setResetErr] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  // Pre-fill from Signup redirect
  useEffect(() => {
    if (location.state?.email) setEmail(location.state.email);
    if (location.state?.password) setPassword(location.state.password);
  }, [location.state]);

  // ✅ Once user is logged in and AuthContext is done, go to Home ("/")
  // Home will decide admin/member routing.
  useEffect(() => {
    if (loading) return;
    if (!user) return;

    navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setResetMsg("");
    setResetErr("");

    setLoginLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);

      // ✅ No role logic + no navigate here.
      // AuthContext will load profile/role and the useEffect above redirects to "/".
    } catch (err) {
      console.error("Login error:", err);

      const code = err?.code;
      if (code === "auth/invalid-credential" || code === "auth/wrong-password") {
        setError("Incorrect email or password.");
      } else if (code === "auth/user-not-found") {
        setError("No account found for that email.");
      } else if (code === "auth/invalid-email") {
        setError("That email address looks invalid.");
      } else if (code === "auth/too-many-requests") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError("Login failed. Please check your email and password.");
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setResetMsg("");
    setResetErr("");
    setError("");

    if (!email) {
      setResetErr("Please enter your email address first.");
      return;
    }

    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setResetMsg("Password reset email sent. Check your inbox (and spam).");
    } catch (err) {
      console.error("Password reset error:", err);

      if (err?.code === "auth/user-not-found") {
        setResetErr("No account found for that email address.");
      } else if (err?.code === "auth/invalid-email") {
        setResetErr("That email address looks invalid.");
      } else if (err?.code === "auth/too-many-requests") {
        setResetErr("Too many attempts. Please try again later.");
      } else {
        setResetErr("Could not send reset email. Please try again.");
      }
    } finally {
      setResetLoading(false);
    }
  };

  // Optional: if already logged in, show a tiny message while redirecting
  if (user && loading) {
    return (
      <div style={{ maxWidth: 400, margin: "40px auto", padding: 20 }}>
        <p>Signing you in…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 400, margin: "40px auto", padding: 20 }}>
      <h2 style={{ fontSize: "24px", marginBottom: "16px", fontWeight: "bold" }}>
        Sign In to Continue
      </h2>

      <form onSubmit={handleLogin}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{ width: "100%", padding: 8, marginBottom: 10 }}
        />

        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={{ width: "100%", padding: 8, marginBottom: 10 }}
        />

        <button
          type="submit"
          disabled={loginLoading}
          style={{
            width: "100%",
            padding: 10,
            background: "#4f46e5",
            color: "white",
            border: "none",
            borderRadius: 4,
            opacity: loginLoading ? 0.7 : 1,
            cursor: loginLoading ? "not-allowed" : "pointer",
          }}
        >
          {loginLoading ? "Logging in..." : "Login"}
        </button>
      </form>

      {/* Forgot password */}
      <div style={{ marginTop: 10, textAlign: "right" }}>
        <button
          type="button"
          onClick={handleForgotPassword}
          disabled={resetLoading}
          style={{
            background: "transparent",
            border: "none",
            color: "#4f46e5",
            cursor: resetLoading ? "not-allowed" : "pointer",
            padding: 0,
            textDecoration: "underline",
            fontSize: 14,
          }}
        >
          {resetLoading ? "Sending..." : "Forgot password?"}
        </button>
      </div>

      {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}
      {resetErr && <p style={{ color: "red", marginTop: 10 }}>{resetErr}</p>}
      {resetMsg && <p style={{ color: "green", marginTop: 10 }}>{resetMsg}</p>}
    </div>
  );
};

export default Login;